import type { DerivedAggregates, WorldState } from "../engine/types";
import { useGame } from "../store/useGame";
import { OrnamentScaffold } from "./Ornaments";
import { Frame } from "./Frame";

interface Props {
  state: WorldState;
  aggregates: DerivedAggregates;
  onNextTurn: () => void;
  allowSkip: boolean;
}

/**
 * Mandat présidentiel français = 5 ans = 60 mois = 60 tours
 * (1 tour = 1 mois depuis la passe rythme de 2026-04-29).
 */
const MANDATE_LENGTH_MONTHS = 60;

function formatDate(turn: number): string {
  // Un tour = un mois, à partir de janvier 2028.
  const months = [
    "JANV.",
    "FÉVR.",
    "MARS",
    "AVRIL",
    "MAI",
    "JUIN",
    "JUIL.",
    "AOÛT",
    "SEPT.",
    "OCT.",
    "NOV.",
    "DÉC.",
  ];
  const base = { month: 0, year: 2028 };
  const totalMonths = base.month + turn;
  const m = totalMonths % 12;
  const y = base.year + Math.floor(totalMonths / 12);
  return `${months[m]} ${y}`;
}

const CRISIS_LABEL: Record<"none" | "rising" | "critical", string> = {
  none: "",
  rising: "CLIMAT POLITIQUE TENDU",
  critical: "CRISE POLITIQUE AIGUË",
};

/** Un bandeau d'alerte unifié (`.alert-chip`). `priority` haut = plus grave. */
interface AlertChip {
  key: string;
  label: string;
  count?: number;
  tone: string;
  priority: number;
  onClick: () => void;
  title: string;
}

/**
 * Le moteur déclenche la crise sur trois signaux (mean_satisfaction, ratio
 * dette/PIB, répression — cf. `update_crisis_level`). Le clic sur l'alerte
 * pointe vers l'indicateur en cause pour qu'on comprenne d'où vient l'info.
 */
function crisisIndicatorTarget(
  state: WorldState,
  aggregates: DerivedAggregates,
): string {
  const debtRatio =
    state.economy.gdp > 0 ? state.economy.public_debt / state.economy.gdp : 0;
  // Satisfaction (et répression, sans indicateur dédié dans la grille) →
  // approbation, qui affiche aussi le ressenti réel. Dette → ratio dette/PIB
  // (révélé à partir de T5).
  if (aggregates.meanSatisfaction < 35) return "approval";
  if (debtRatio > 1.3 && state.turn >= 5) return "debt_ratio";
  return "approval";
}

/**
 * Europe S2 — état des procédures européennes en cours, projeté sur une
 * échelle qualitative pour l'indicateur 🇪🇺 du bandeau. La gravité est
 * dérivée de l'id : Article 7 / fonds gelés / MES tutelle = critique,
 * sinon majeure si ≥ 2 procédures, sinon mineure.
 */
const CRITICAL_EU_IDS = new Set([
  "eu_article_7",
  "eu_fonds_geles",
  "eu_mes_tutelle",
]);

function brusselsState(interventionIds: string[]): {
  label: string;
  tone: "neutral" | "watch" | "alert" | "danger";
} | null {
  if (interventionIds.length === 0) return null;
  const hasCritical = interventionIds.some((id) => CRITICAL_EU_IDS.has(id));
  if (hasCritical && interventionIds.length >= 2) {
    return { label: "🇪🇺 MISE SOUS TUTELLE PROCHE", tone: "danger" };
  }
  if (hasCritical) {
    return { label: "🇪🇺 SANCTION EUROPÉENNE ACTIVE", tone: "danger" };
  }
  if (interventionIds.length >= 2) {
    return { label: "🇪🇺 PROCÉDURES EUROPÉENNES MULTIPLES", tone: "alert" };
  }
  return { label: "🇪🇺 BRUXELLES INTERVIENT", tone: "watch" };
}

export function MetaBar({ state, aggregates, onNextTurn, allowSkip }: Props) {
  const openIndicator = useGame((s) => s.openIndicator);
  const regimeLabel =
    state.polity.regime === "democracy" ? "Ve RÉPUBLIQUE" : "ÉTAT D'EXCEPTION";

  // K2 — bandeau d'alerte sous la MetaBar selon le niveau d'escalade.
  const crisis = state.polity.crisis_level ?? "none";

  // L'alerte mène à l'indicateur en cause. On scrolle d'abord vers SA cellule
  // (centrée) et on n'ouvre la sheet de détail qu'ensuite : sinon le fond modal
  // (backdrop plein écran) s'ouvre instantanément et masque le scroll — le
  // joueur ne voit jamais la page bouger.
  const onCrisisClick = () => {
    const target = crisisIndicatorTarget(state, aggregates);
    const cell =
      document.querySelector(`[data-indicator-id="${target}"]`) ??
      document.querySelector(".indicator-strip");
    cell?.scrollIntoView({ behavior: "smooth", block: "center" });
    // Laisse le scroll se dérouler avant d'ouvrir le détail.
    window.setTimeout(() => openIndicator(target), 480);
  };

  // Europe S2 — indicateur Bruxelles. Affiché dès la première procédure
  // européenne active, reste jusqu'à fin de mandat (append-only côté Rust).
  const euIds = (state.polity.european_interventions ?? []).map((iv) => iv.id);
  const eu = brusselsState(euIds);

  // Le bandeau (quel que soit son état) mène au drawer « Bruxelles » qui
  // détaille les procédures. Pas de backdrop ici (drawer inline) → on ouvre
  // et on scrolle directement, sans délai.
  const onEuClick = () => {
    const drawer = document.querySelector(".drawer-eu");
    if (drawer instanceof HTMLDetailsElement) drawer.open = true;
    drawer?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Toutes les alertes partagent le même chip et s'affichent les unes à la
  // suite des autres, triées par importance décroissante. `priority` plus haut
  // = plus grave. Le comportement au clic reste propre à chaque alerte.
  const alerts: AlertChip[] = [];
  if (crisis !== "none") {
    alerts.push({
      key: "crisis",
      label: CRISIS_LABEL[crisis],
      tone: crisis === "critical" ? "crisis-critical" : "crisis-rising",
      priority: crisis === "critical" ? 100 : 60,
      onClick: onCrisisClick,
      title: "Voir l'indicateur à l'origine de l'alerte",
    });
  }
  if (eu) {
    alerts.push({
      key: "eu",
      label: eu.label,
      count: euIds.length,
      tone:
        eu.tone === "danger"
          ? "eu-danger"
          : eu.tone === "alert"
            ? "eu-alert"
            : "eu-watch",
      priority: eu.tone === "danger" ? 90 : eu.tone === "alert" ? 50 : 30,
      onClick: onEuClick,
      title: `${euIds.length} procédure(s) européenne(s) active(s) — voir le détail`,
    });
  }
  alerts.sort((a, b) => b.priority - a.priority);

  return (
    <>
      {/* Refonte « page de presse 1888 » (2026-08-02) — manchette de
          quotidien illustré : deux encadrés flanquent le titre gothique,
          comme les réclames qui encadraient la une. À gauche l'avancée du
          mandat (vignette de chantier + mois), à droite le régime. Sous le
          titre, le volume du mandat : soixante crans dont N encrés. */}
      <div className="masthead">
        {/* Bandeau de prix — le premier filet de la page, comme sur les
            illustrés : deux filets doubles encadrent une mention en très
            petit corps espacé. */}
        <div className="masthead-price-band" aria-hidden="true">
          <span className="mpb-rule" />
          <span className="mpb-mark">➤</span>
          <span className="mpb-text">Prix : une décision</span>
          <span className="mpb-mark mpb-mark-flip">➤</span>
          <span className="mpb-rule" />
        </div>

        <div className="masthead-core">
          <h1 className="masthead-title">Moi Président</h1>
          <div className="masthead-kicker-row">
            <span className="mk-rule" aria-hidden="true" />
            <span className="masthead-kicker">
              Journal de la nation et des affaires
            </span>
            <span className="mk-rule" aria-hidden="true" />
          </div>
          <div className="masthead-fleurons" aria-hidden="true">
            ❖&nbsp;✦&nbsp;❖
          </div>
        </div>

        <div className="masthead-flanks">
          <Frame variant="panel" corners="diagonal" className="masthead-flank">
            <OrnamentScaffold size={28} className="masthead-vignette" />
            <span className="masthead-flank-value">{state.turn + 1}</span>
            <span className="masthead-flank-side">
              <span className="masthead-flank-label">Mois de mandat</span>
              <span className="masthead-flank-total">sur soixante</span>
              <span className="masthead-flank-note">Rien ne s'annule.</span>
            </span>
          </Frame>

          <Frame
            variant="panel"
            corners="diagonal"
            className="masthead-flank masthead-flank-right"
          >
            <span className="masthead-flank-label">Régime</span>
            <span className="masthead-flank-regime">{regimeLabel}</span>
            <span className="masthead-flank-sep" aria-hidden="true">
              <span className="mfs-rule" />✦<span className="mfs-rule" />
            </span>
            <span className="masthead-flank-poll">
              Approbation{" "}
              {Math.round(
                state.polity.approval_rate ?? aggregates.meanSatisfaction,
              )}
              &nbsp;%
            </span>
          </Frame>
        </div>

        {/* Volume du mandat — soixante crans. Le courant est plus haut et en
            garance, les débuts d'année plus hauts que les mois ordinaires :
            on lit l'avancée sans lire un chiffre. Statique, jamais animé. */}
        <div className="masthead-crans" aria-hidden="true">
          {Array.from({ length: MANDATE_LENGTH_MONTHS }, (_, i) => (
            <span
              key={i}
              className="cran"
              data-state={
                i === state.turn ? "now" : i < state.turn ? "past" : "todo"
              }
              data-year={i % 12 === 0 ? "" : undefined}
            />
          ))}
        </div>
        <div className="masthead-crans-marks" aria-hidden="true">
          <span>Prise de fonctions</span>
          <span>Mi‑mandat</span>
          <span>Présidentielles</span>
        </div>

        <div className="masthead-edition">
          <span className="masthead-issue">N° {state.turn + 1} — Vol. I</span>
          <span className="masthead-date">
            <span className="med-mark" aria-hidden="true">
              ➤
            </span>
            {formatDate(state.turn)}
            <span className="med-mark med-mark-flip" aria-hidden="true">
              ➤
            </span>
          </span>
          <span className="masthead-scrutin">
            {(() => {
              const r = Math.max(0, MANDATE_LENGTH_MONTHS - state.turn);
              if (r === 0) return "Scrutin imminent";
              if (r === 1) return "Scrutin dans 1 mois";
              return `Scrutin dans ${r} mois`;
            })()}
          </span>
        </div>
      </div>
      {/* L'ours ne porte plus que l'action : mandat et échéance sont passés
          dans la ligne d'édition de la manchette (2026-08-06), et la barre
          disparaît quand il n'y a rien à y mettre — une rangée entière de
          gagnée sur la hauteur mobile. */}
      {allowSkip && (
        <header className="meta-bar">
          <span className="meta-spacer" />
          <button className="meta-action" onClick={onNextTurn}>
            Tour suivant →
          </button>
        </header>
      )}
      {alerts.length > 0 && (
        <div className="meta-alerts">
          {alerts.map((a) => (
            <button
              key={a.key}
              type="button"
              className="alert-chip"
              data-tone={a.tone}
              onClick={a.onClick}
              title={a.title}
            >
              <span className="alert-chip-label">{a.label}</span>
              {a.count != null && (
                <span className="alert-chip-count">{a.count}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </>
  );
}
